/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetProposalJobRow } from '../openapi-types.gen.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';


// ─── useGetProposalJob ────────────────────────────────────────────────────────────

export interface useGetProposalJobArgs { readonly jobId: string; readonly fields?: readonly ('jobId' | 'status' | 'lastSeq' | 'text' | 'images' | 'proposal' | 'classification' | 'error')[]; }


async function _useGetProposalJobQueryFn(
  args: useGetProposalJobArgs,
  ctx: QueryFunctionContext,
): Promise<GetProposalJobRow | null> {
  if (args.jobId === undefined || args.jobId === null || args.jobId === '') return null;
  const url = `/ai/proposal/jobs/${args.jobId}`;
  const params = new URLSearchParams();
  // FQ-DETAIL-SHARED-ENTRY-001 — fetch the full row; `args.fields` is
  // intentionally NOT forwarded (shared id-only cache entry).
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetProposalJob',
    routeTemplate: '/ai/proposal/jobs/{jobId}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetProposalJobRow;
}


const _useGetProposalJobFactory = Object.assign(
  (args: useGetProposalJobArgs) => ({
    queryKey: ['ai', 'detail', args.jobId, 'jobs'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetProposalJobQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetProposalJob',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['ai', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetProposalJobArgs, GetProposalJobRow>;


export const useGetProposalJobSpec = {
  __brand: 'HookSpec' as const,
  path: '/ai/proposal/jobs/{jobId}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetProposalJobFactory },
  parseInput: (raw: unknown): useGetProposalJobArgs => raw as useGetProposalJobArgs,
  parseResponse: (raw: unknown): GetProposalJobRow => raw as GetProposalJobRow,
  specName: 'useGetProposalJob' as const,
  polling: { hasNats: false, intervalMs: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetProposalJobRow>;
